import { isFileUid } from '@/utils/fileUrl';

/**
 * The credential fields the registration and account forms send to keyon.
 * Files are referenced by keyon's opaque uid only; no path or URL is ever sent.
 */

// UploadNurseFile's categories (plan §7.1): misc stays the endpoint default for
// an upload that is not a license or insurance slot.
export const IMMEDIATE_UPLOAD_TYPES = ['license', 'insurance', 'misc'];

function present(value) {
  return value !== undefined && value !== null && String(value).trim() !== '';
}

/**
 * UploadNurseFile: stores the file right away and answers with its `file_uid`,
 * which the form then sends in place of the file. `subaccnt` is the person the
 * file belongs to, `accountnumber` their account. Post it with no Content-Type:
 * the browser writes the multipart boundary.
 * @throws {Error} on a missing file, an unknown pdftype, or a missing account or person
 */
export function buildUploadNurseFileForm({ file, pdftype, accountnumber, subaccnt } = {}, FormDataImpl = FormData) {
  if (!file) throw new Error('buildUploadNurseFileForm: no file');
  if (!IMMEDIATE_UPLOAD_TYPES.includes(pdftype)) throw new Error('buildUploadNurseFileForm: unknown pdftype');
  if (!present(accountnumber) || !present(subaccnt)) {
    throw new Error('buildUploadNurseFileForm: accountnumber and subaccnt are required');
  }
  const fd = new FormDataImpl();
  fd.append('API_Meth', 'UploadNurseFile');
  fd.append('pdftype', pdftype);
  fd.append('files', file);
  fd.append('subaccnt', String(subaccnt));
  fd.append('accountnumber', String(accountnumber));
  return fd;
}

/**
 * CreateSubAccount's two credential slots. keyon refuses a uid and a file for
 * the same slot (400 file_and_uid), so per slot the uid from an immediate
 * upload wins and the file is left out; with no valid uid the picked file, if
 * any, is sent.
 */
export function appendCredentialSlots(formData, { licenseUid, insuranceUid, licenseFile, insuranceFile } = {}) {
  if (isFileUid(licenseUid)) formData.append('license_file_uid', licenseUid);
  else if (licenseFile) formData.append('licenseFile', licenseFile);
  if (isFileUid(insuranceUid)) formData.append('insurance_file_uid', insuranceUid);
  else if (insuranceFile) formData.append('insuranceFile', insuranceFile);
  return formData;
}

/**
 * InternalUpdateNurse: a uid key only for a slot uploaded during this edit. An
 * absent key leaves the stored reference untouched.
 */
export function changedUidKeys({ licenseUid, insuranceUid } = {}) {
  const keys = {};
  if (isFileUid(licenseUid)) keys.license_file_uid = licenseUid;
  if (isFileUid(insuranceUid)) keys.insurance_file_uid = insuranceUid;
  return keys;
}

/**
 * The uids appendCredentialSlots put on a form: what a CreateSubAccount that
 * succeeds has claimed, for unclaimedUploads().saved.
 */
export function sentCredentialUids(formData) {
  return ['license_file_uid', 'insurance_file_uid'].map((key) => formData.get(key)).filter(isFileUid);
}

/**
 * The immediate uploads a form holds and has not saved, one per slot (the
 * nwform key its uid lives under). UploadNurseFile stores a file the moment it
 * is picked, and it stays unclaimed until the form is saved, so:
 * - a file that replaces another in its slot releases the one it replaced;
 * - closing the form without saving (releaseAll, from the component's
 *   unmount) releases whatever is still held, and an upload that lands after
 *   that is released as soon as it arrives;
 * - saved(uids) forgets the uids that save sent: they belong to the saved
 *   record now, and keyon retires a saved file itself when a later save
 *   replaces it. An upload that arrived during the save and was not sent by it
 *   is still the form's.
 * Between beginSave() and endSave() nothing is released. The save may be
 * carrying the very file a mid-save replacement displaced, and a release that
 * reached keyon first would discard it under the save (CreateSubAccount then
 * answers invalid_file_uid, after the login account already exists). What
 * would have been released waits, and endSave() releases it, less what the
 * save claimed - and everything still held, if the form closed meanwhile.
 * Saves nest; the outermost endSave() is the one that releases.
 * `discard` is keyonFiles.discardUnclaimedCredentialUpload. keyon only ever
 * removes the caller's own unclaimed upload, so releasing a uid that was in
 * fact saved is refused there and harmless. Nothing here throws or waits:
 * releasing is best-effort and the sweep is the backstop.
 */
export function unclaimedUploads(discard) {
  const held = new Map();
  const waiting = new Set();
  let saves = 0;
  let closed = false;
  const release = (uid) => {
    try {
      const pending = discard(uid);
      if (pending && typeof pending.catch === 'function') pending.catch(() => undefined);
    } catch (_) {
      // Best-effort; a release that fails is the sweep's.
    }
  };
  const releaseAfterSaves = (uid) => {
    if (saves > 0) waiting.add(uid);
    else release(uid);
  };
  const releaseHeld = () => {
    const uids = Array.from(held.values());
    held.clear();
    uids.forEach(release);
  };
  return {
    hold(slot, uid) {
      if (!isFileUid(uid)) return;
      if (closed) {
        releaseAfterSaves(uid);
        return;
      }
      const previous = held.get(slot);
      held.set(slot, uid);
      if (previous && previous !== uid) releaseAfterSaves(previous);
    },
    beginSave() {
      saves += 1;
    },
    saved(uids = []) {
      uids.forEach((uid) => {
        waiting.delete(uid);
        held.forEach((heldUid, slot) => {
          if (heldUid === uid) held.delete(slot);
        });
      });
    },
    endSave() {
      if (saves === 0) return;
      saves -= 1;
      if (saves > 0) return;
      const uids = Array.from(waiting);
      waiting.clear();
      uids.forEach(release);
      if (closed) releaseHeld();
    },
    releaseAll() {
      closed = true;
      if (saves === 0) releaseHeld();
    },
    uids() {
      return Array.from(held.values());
    },
  };
}

/**
 * Promoting a Home Health Aide: the insurance uploaded during this edit, else
 * the one already on the account user, else nothing.
 */
export function promoteInsuranceUid(uploadedUid, accountUser) {
  if (isFileUid(uploadedUid)) return uploadedUid;
  const existing = accountUser && accountUser.insurance_file_uid;
  return isFileUid(existing) ? existing : null;
}
